import { useState, type ReactNode } from "react";

import { useUpdateCollection } from "../api/collections";
import type { Collection } from "../api/types";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { Field, TextField } from "../ui/Field";
import { Select } from "../ui/Select";
import { Toggle } from "../ui/Toggle";
import { useToast } from "../ui/toast-context";

/** The sort options a collection may store (`docs/spec/collections.md` §2.5). */
const SORT_OPTIONS = [
  { value: "index:asc", label: "Manual order" },
  { value: "title:asc", label: "Title, A–Z" },
  { value: "title:desc", label: "Title, Z–A" },
  { value: "updatedAt:desc", label: "Recently updated" },
  { value: "createdAt:desc", label: "Recently created" },
] as const;

const PERMISSIONS = [
  { value: "read_write", label: "Everyone can edit" },
  { value: "read", label: "Everyone can read" },
  { value: "none", label: "Members only" },
] as const;

const COMMENTING = [
  { value: "default", label: "Follow the workspace setting" },
  { value: "on", label: "Comments allowed" },
  { value: "off", label: "Comments off" },
] as const;

export function EditCollectionDialog({
  open,
  collection,
  onClose,
}: {
  open: boolean;
  collection: Collection;
  onClose: () => void;
}): ReactNode {
  const update = useUpdateCollection();
  const toast = useToast();
  const [name, setName] = useState(collection.name);
  const [icon, setIcon] = useState(collection.icon ?? "");
  const [color, setColor] = useState(collection.color ?? "#4E5C6E");
  const [permission, setPermission] = useState<string>(collection.permission ?? "none");
  const [sharing, setSharing] = useState(collection.sharing !== false);
  const [commenting, setCommenting] = useState<string>(
    collection.commenting === undefined || collection.commenting === null
      ? "default"
      : collection.commenting
        ? "on"
        : "off",
  );
  const [sort, setSort] = useState<string>(
    `${collection.sort?.field ?? "index"}:${collection.sort?.direction ?? "asc"}`,
  );

  const submit = (): void => {
    const [field = "index", direction = "asc"] = sort.split(":");
    update.mutate(
      {
        id: collection.id,
        name: name.trim(),
        icon: icon.trim() === "" ? null : icon.trim(),
        color,
        permission: permission === "none" ? null : (permission as "read" | "read_write"),
        sharing,
        commenting: commenting === "default" ? null : commenting === "on",
        sort: { field, direction },
      },
      {
        onSuccess: () => {
          toast.show("Collection updated");
          onClose();
        },
        onError: (error) => {
          toast.show(error.message, "error");
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} title="Edit collection" width="sm">
      <div className="flex flex-col gap-3">
        <TextField label="Name" value={name} onChange={setName} />
        <TextField label="Icon" description="A single emoji." value={icon} onChange={setIcon} />
        <Field label="Colour">
          {(id) => (
            <input
              id={id}
              type="color"
              value={color}
              onChange={(event) => {
                setColor(event.target.value);
              }}
              className="h-9 w-16 rounded-md border border-line bg-raised"
            />
          )}
        </Field>
        <Select
          label="Workspace access"
          value={permission}
          options={PERMISSIONS}
          onChange={setPermission}
        />
        <Select label="Default sort" value={sort} options={SORT_OPTIONS} onChange={setSort} />
        <Select
          label="Commenting"
          value={commenting}
          options={COMMENTING}
          onChange={setCommenting}
        />
        <Toggle
          label="Allow public sharing"
          description="Documents in this collection may be published to a public link."
          checked={sharing}
          onChange={setSharing}
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={update.isPending || name.trim() === ""}
            onClick={submit}
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
